import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getOffer, createOffer, updateOffer, getOrderClients, suggestTimes, offerToOrder } from '../services/orders';
import { getEnabledOperations } from '../services/operations';
import { getAccountSettings } from '../services/settings';
import ClientPicker from '../components/common/ClientPicker';
import './OfferPage.css';

const MINUTOS_HORA = 60;

// Los que pidió la clienta: nº de piezas, diámetros, conos, demasía, caras y longitud.
const PARAMETROS = [
  { key: 'piezas', label: 'Nº de piezas', type: 'number', min: 1 },
  { key: 'longitud_mm', label: 'Longitud (mm)', type: 'number' },
  { key: 'diametro_mm', label: 'Diámetro (mm)', type: 'number' },
  { key: 'demasia_mm', label: 'Demasía (mm)', type: 'number' },
  { key: 'caras', label: 'Caras', type: 'number' },
  { key: 'diametros', label: 'Diámetros a rectificar', type: 'number' },
];

// Qué palabras del pedido delatan cada trabajo. Sirve para sugerir, nunca para decidir:
// la última palabra es siempre de quien presupuesta.
const PISTAS = {
  RECTIFICAR_DIAMETROS: ['diámetro', 'diametro', 'ø', 'exterior', 'rectificado exterior'],
  RECTIFICAR_ESTRIAS: ['estría', 'estria'],
  RECTIFICAR_FLANCOS: ['flanco', 'dentado', 'diente'],
  TALLAR_ESTRIAS: ['tallar', 'tallado'],
};

const sugerencias = (texto) => {
  const t = (texto || '').toLowerCase();
  if (!t) return new Set();
  return new Set(Object.entries(PISTAS)
    .filter(([, palabras]) => palabras.some((p) => t.includes(p)))
    .map(([clave]) => clave));
};

const CALCULADORA_VACIA = {
  piezas: 1, longitud_mm: '', diametro_mm: '', conica: false,
  demasia_mm: '', caras: '', diametros: '', precio_hora: '', lineas: [],
};

const numero = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const formatHoras = (h) => `${h.toFixed(2).replace('.', ',')} h`;
const formatFecha = (valor) => (valor ? new Date(valor).toLocaleDateString('es-ES') : '—');
const formatEuros = (v) => v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const OfferPage = () => {
  const { offerHash } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const analysisHash = searchParams.get('analysis');
  // LAKPED-16.1: cuando se viene de clasificar un documento, el cliente y la pieza ya se saben.
  const clienteDelDocumento = searchParams.get('cliente');
  const piezaDelDocumento = searchParams.get('pieza');

  const [calc, setCalc] = useState(CALCULADORA_VACIA);
  const [cabecera, setCabecera] = useState({
    client: clienteDelDocumento || '',
    reference: '',
    description: piezaDelDocumento || '',
    notes: '',
  });
  const [clients, setClients] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [precedente, setPrecedente] = useState(null);
  const [verPedidos, setVerPedidos] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(Boolean(offerHash));
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!offerHash) return;
    try {
      const data = await getOffer(offerHash);
      setOffer(data);
      setCabecera({
        client: data.client?.name || '',
        reference: data.reference || '',
        description: data.description || '',
        notes: data.notes || '',
      });
      setCalc({ ...CALCULADORA_VACIA, ...(data.calculator || {}) });
    } catch (err) {
      setError('No se ha podido cargar la oferta');
    } finally {
      setLoading(false);
    }
  }, [offerHash]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { getOrderClients().then(setClients).catch(() => {}); }, []);
  // El logo solo se ve en la hoja impresa, que es la que sale de casa
  useEffect(() => {
    getAccountSettings()
      .then((s) => setLogoUrl(s.logo_url ? `${process.env.REACT_APP_API_URL}${s.logo_url}` : null))
      .catch(() => setLogoUrl(null));
  }, []);
  // Busca precedentes al dejar de escribir. No rellena nada: solo enseña lo que ya pasó.
  useEffect(() => {
    const texto = cabecera.description.trim();
    setVerPedidos(false);
    if (texto.length < 3) { setPrecedente(null); return undefined; }
    const timer = setTimeout(() => {
      suggestTimes(texto, cabecera.client, analysisHash || offer?.analysis?.hash)
        .then((d) => setPrecedente(d.hay_propuesta ? d : null))
        .catch(() => setPrecedente(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [cabecera.description, cabecera.client, analysisHash, offer]);

  useEffect(() => {
    getEnabledOperations()
      .then((d) => setOperaciones(d.operations || []))
      .catch(() => setOperaciones([]));
  }, []);

  // El mismo cálculo que hace el servidor, para que el total se vea mientras se escribe
  const minutosPieza = (calc.lineas || []).reduce((suma, l) => suma + (Number(l.minutos_pieza) || 0), 0);
  const horas = (minutosPieza / MINUTOS_HORA) * (Number(calc.piezas) || 0);
  const importe = calc.precio_hora ? horas * Number(calc.precio_hora) : null;

  const actualizar = (key, value) => setCalc((prev) => ({ ...prev, [key]: value }));

  const sugeridas = sugerencias(`${cabecera.description} ${cabecera.notes}`);
  const minutosDe = (clave) => {
    const linea = (calc.lineas || []).find((l) => l.clave === clave);
    return linea ? linea.minutos_pieza : '';
  };

  const fijarMinutos = (clave, concepto, minutos) => setCalc((prev) => {
    const otras = (prev.lineas || []).filter((l) => l.clave !== clave);
    if (minutos === '' || Number(minutos) === 0) return { ...prev, lineas: otras };
    return { ...prev, lineas: [...otras, { clave, concepto, minutos_pieza: minutos }] };
  });

  const actualizarLinea = (indice, campo, valor) => setCalc((prev) => ({
    ...prev,
    lineas: prev.lineas.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)),
  }));

  const añadirLinea = () => setCalc((prev) => ({
    ...prev, lineas: [...prev.lineas, { concepto: '', minutos_pieza: '' }],
  }));

  const quitarLinea = (indice) => setCalc((prev) => ({
    ...prev, lineas: prev.lineas.filter((_, i) => i !== indice),
  }));

  const cambiarEstado = async (status) => {
    setSaving(true);
    setError(null);
    try {
      await updateOffer(offerHash, { status });
      await cargar();
    } catch (e) {
      setError('No se ha podido cambiar el estado de la oferta');
    } finally {
      setSaving(false);
    }
  };

  const convertirEnPedido = async () => {
    setConverting(true);
    setError(null);
    try {
      // Primero se guarda lo que hay en pantalla: si no, el pedido nacería con lo anterior
      // y lo editado se perdería sin que nadie avise. Si el guardado falla, no se convierte.
      if (!await guardar()) return;
      const order = await offerToOrder(offerHash);
      navigate(`/orders/${order.hash}`);
    } catch (e) {
      setError(e.message || 'No se ha podido crear el pedido');
    } finally {
      setConverting(false);
    }
  };

  const guardar = async () => {
    setSaving(true);
    setError(null);
    const matched = clients.find((c) => c.name.toLowerCase() === cabecera.client.trim().toLowerCase());
    const payload = {
      reference: cabecera.reference || null,
      description: cabecera.description || null,
      notes: cabecera.notes || null,
      calculator: {
        piezas: Number(calc.piezas) || 1,
        longitud_mm: numero(calc.longitud_mm),
        diametro_mm: numero(calc.diametro_mm),
        conica: Boolean(calc.conica),
        demasia_mm: numero(calc.demasia_mm),
        caras: numero(calc.caras),
        diametros: numero(calc.diametros),
        precio_hora: numero(calc.precio_hora),
        lineas: (calc.lineas || [])
          .filter((l) => l.concepto.trim())
          .map((l) => ({ clave: l.clave || null, concepto: l.concepto.trim(), minutos_pieza: Number(l.minutos_pieza) || 0 })),
      },
    };
    try {
      if (offerHash) {
        await updateOffer(offerHash, { ...payload, client_hash: matched?.hash });
        await cargar();
      } else {
        const creada = await createOffer({
          ...payload,
          analysis_hash: analysisHash || null,
          client_hash: matched?.hash || null,
          client_name: matched ? null : cabecera.client.trim() || null,
        });
        navigate(`/offers/${creada.hash}`, { replace: true });
      }
      return true;
    } catch (err) {
      setError('No se ha podido guardar la oferta');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="offer-container"><p className="offer-loading">Cargando…</p></div>;

  return (
    <div className="offer-container">
      <button className="offer-back" onClick={() => navigate('/orders')}>‹ Volver a pedidos</button>
      {logoUrl && <img src={logoUrl} alt="" className="offer-print-logo" />}
      <div className="offer-head">
        <h1 className="offer-title">{offerHash ? 'Oferta' : 'Nueva oferta'}</h1>
        {offerHash && (
          <span className="offer-print-date">
            {new Date(offer?.created || Date.now()).toLocaleDateString('es-ES')}
          </span>
        )}
        {offer && <span className={`offer-badge ${offer.status}`}>{
          { draft: 'Borrador', sent: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', expired: 'Caducada' }[offer.status] || offer.status
        }</span>}
      </div>

      {error && <div className="offer-error">{error}</div>}

      <section className="offer-card">
        <h2>Datos</h2>
        <div className="offer-grid">
          <div className="offer-field offer-field-wide">
            <label>Cliente</label>
            <ClientPicker id="offer-cliente" clients={clients} value={cabecera.client}
                          onChange={(nombre) => setCabecera({ ...cabecera, client: nombre })} />
          </div>
          <div className="offer-field">
            <label>Referencia</label>
            <input value={cabecera.reference}
                   onChange={(e) => setCabecera({ ...cabecera, reference: e.target.value })} />
          </div>
          <div className="offer-field offer-field-full">
            <label>Pieza / trabajo</label>
            <input value={cabecera.description}
                   onChange={(e) => setCabecera({ ...cabecera, description: e.target.value })}
                   placeholder="Ej. Eje rectificado Ø65" />
          </div>
        </div>
      </section>

      <section className="offer-card">
        <h2>La pieza</h2>
        <div className="offer-grid">
          {PARAMETROS.map((p) => (
            <div className="offer-field" key={p.key}>
              <label>{p.label}</label>
              <input type={p.type} min={p.min} value={calc[p.key] ?? ''}
                     onChange={(e) => actualizar(p.key, e.target.value)} />
            </div>
          ))}
          <label className="offer-check">
            <input type="checkbox" checked={Boolean(calc.conica)}
                   onChange={(e) => actualizar('conica', e.target.checked)} />
            <span>Pieza cónica</span>
          </label>
        </div>
      </section>

      {precedente && (
        <div className={`offer-precedente${precedente.grupo_disperso ? ' offer-precedente-cuidado' : ''}`}>
          <div className="offer-precedente-texto">
            <strong>{precedente.grupo_disperso ? 'Cuidado con esta descripción.' : 'Esta pieza ya tiene historia.'}</strong>{' '}
            {precedente.grupo_disperso
              ? `Hay ${precedente.casos} pedidos con esa descripción, pero de ${precedente.clientes_distintos} clientes distintos: no parecen la misma pieza, así que no se da una media de horas. Pon el cliente arriba para ver solo los suyos.`
              : precedente.horas_por_pieza
                ? `Se ha fabricado ${precedente.pedidos_total} ${precedente.pedidos_total === 1 ? 'vez' : 'veces'} y costó ${precedente.horas_por_pieza.toFixed(2).replace('.', ',')} h por pieza.`
                : `Se ha presupuestado ${precedente.casos} ${precedente.casos === 1 ? 'vez' : 'veces'} antes.`}
            {precedente.ambito === 'plano' && precedente.pedidos_total > 1 && <span> Son pedidos del mismo documento, no de una descripción parecida.</span>}
            {precedente.ambito === 'plano' && precedente.pedidos_total === 1 && <span> Es el pedido anterior de este mismo documento.</span>}
            {precedente.ambito === 'cliente' && <span> Solo se cuentan los de este cliente: esa descripción la usan varios.</span>}
            {precedente.horas_habitual_min != null
              && precedente.horas_habitual_min !== precedente.horas_habitual_max && (
              <span>
                {' '}La mitad de las veces costó entre {formatHoras(precedente.horas_habitual_min)} y {formatHoras(precedente.horas_habitual_max)}.
              </span>
            )}
            {precedente.correccion && (
              <span className="offer-precedente-aviso">
                {' '}Ojo: lo presupuestado se queda {precedente.correccion.factor > 1 ? 'corto' : 'largo'} de
                media (factor {String(precedente.correccion.factor).replace('.', ',')} sobre {precedente.correccion.casos} casos).
              </span>
            )}
          </div>
          {precedente.pedidos?.length > 0 && (
            <button className="offer-precedente-ver" onClick={() => setVerPedidos((v) => !v)}>
              {verPedidos ? 'Ocultar los pedidos' : `Ver los ${precedente.pedidos_total} pedidos`}
            </button>
          )}
          {precedente.minutos_por_operacion && (
            <button className="offer-precedente-aplicar" onClick={() => {
              const nuevas = Object.entries(precedente.minutos_por_operacion).map(([clave, minutos]) => ({
                clave, concepto: (operaciones.find((o) => o.key === clave) || {}).name || clave, minutos_pieza: minutos,
              }));
              setCalc((prev) => ({ ...prev, lineas: [...(prev.lineas || []).filter((l) => !l.clave), ...nuevas] }));
            }}>Usar esos tiempos</button>
          )}
          {verPedidos && precedente.pedidos?.length > 0 && (
            <table className="offer-precedente-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nº de pedido</th>
                  <th className="col-num">Piezas</th>
                  <th className="col-num">Horas</th>
                  <th className="col-num">Por pieza</th>
                </tr>
              </thead>
              <tbody>
                {precedente.pedidos.map((pedido) => (
                  <tr key={pedido.hash} className="offer-precedente-fila"
                      title="Abrir el pedido en otra pestaña"
                      onClick={(e) => {
                        // El enlace de la fecha ya abre solo: si no, saldrían dos pestañas
                        if (!e.target.closest('a')) window.open(`/orders/${pedido.hash}`, '_blank', 'noreferrer');
                      }}>
                    <td>
                      <a href={`/orders/${pedido.hash}`} target="_blank" rel="noreferrer">{formatFecha(pedido.fecha)}</a>
                    </td>
                    <td>{pedido.referencia_cliente || '—'}</td>
                    <td className="col-num">{pedido.piezas}</td>
                    <td className="col-num">{formatHoras(pedido.horas)}</td>
                    <td className="col-num">{formatHoras(pedido.horas_por_pieza)}</td>
                  </tr>
                ))}
              </tbody>
              {precedente.pedidos_total > precedente.pedidos.length && (
                <tfoot>
                  <tr>
                    <td colSpan="5">Y otros {precedente.pedidos_total - precedente.pedidos.length} pedidos anteriores.</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      )}

      <section className="offer-card">
        <h2>Tiempos</h2>
        {operaciones.length > 0 && (
          <table className="offer-lines">
            <thead>
              <tr>
                <th>Trabajo</th>
                <th className="col-num">Minutos por pieza</th>
                <th className="col-num">Total</th>
              </tr>
            </thead>
            <tbody>
              {operaciones.map((op) => {
                const minutos = minutosDe(op.key);
                const sugerida = sugeridas.has(op.key);
                return (
                  <tr key={op.key} className={minutos !== '' ? 'activa' : ''}>
                    <td>
                      <span className="offer-op-nombre">{op.name}</span>
                      {sugerida && minutos === '' && (
                        <span className="offer-op-pista" title="El pedido menciona este trabajo">sugerido</span>
                      )}
                    </td>
                    <td className="col-num">
                      <input type="number" min="0" step="0.5" value={minutos} placeholder="—"
                             onChange={(e) => fijarMinutos(op.key, op.name, e.target.value)} />
                    </td>
                    <td className="col-num offer-line-total">
                      {minutos === '' ? '—'
                        : formatHoras(((Number(minutos) || 0) / MINUTOS_HORA) * (Number(calc.piezas) || 0))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {operaciones.length === 0 && (
          <p className="offer-empty">Esta cuenta no tiene trabajos configurados, así que no hay nada que calcular.</p>
        )}

        {(calc.lineas || []).filter((l) => !l.clave).length > 0 && (
          <table className="offer-lines">
            <tbody>
              {(calc.lineas || []).map((linea, i) => linea.clave ? null : (
                <tr key={i}>
                  <td>
                    <input value={linea.concepto} placeholder="Otro trabajo"
                           onChange={(e) => actualizarLinea(i, 'concepto', e.target.value)} />
                  </td>
                  <td className="col-num">
                    <input type="number" min="0" step="0.5" value={linea.minutos_pieza}
                           onChange={(e) => actualizarLinea(i, 'minutos_pieza', e.target.value)} />
                  </td>
                  <td className="col-num offer-line-total">
                    {formatHoras(((Number(linea.minutos_pieza) || 0) / MINUTOS_HORA) * (Number(calc.piezas) || 0))}
                  </td>
                  <td className="col-num">
                    <button className="offer-remove" onClick={() => quitarLinea(i)} title="Quitar">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <button className="offer-add" onClick={añadirLinea}>+ Añadir otro trabajo</button>
      </section>

      <section className="offer-card offer-total-card">
        <div className="offer-total-line">
          <span>Tiempo por pieza</span>
          <strong>{formatHoras(minutosPieza / MINUTOS_HORA)}</strong>
        </div>
        <div className="offer-total-line">
          <span>Total {calc.piezas || 0} pieza{Number(calc.piezas) === 1 ? '' : 's'}</span>
          <strong className="offer-total-horas">{formatHoras(horas)}</strong>
        </div>
        {precedente?.horas_por_pieza && (
          <div className="offer-total-line offer-total-comparacion">
            <span>Histórico de esta pieza</span>
            <strong>{formatHoras(precedente.horas_por_pieza * (Number(calc.piezas) || 0))}</strong>
          </div>
        )}
        <div className="offer-total-line">
          <span>Precio por hora</span>
          <input className="offer-precio" type="number" min="0" step="0.01" value={calc.precio_hora ?? ''}
                 onChange={(e) => actualizar('precio_hora', e.target.value)} placeholder="€/h" />
        </div>
        <div className="offer-total-line offer-total-importe">
          <span>Importe de la oferta</span>
          <strong>{importe === null ? '—' : formatEuros(importe)}</strong>
        </div>
      </section>

      <div className="offer-actions">
        <button className="offer-cancel" onClick={() => navigate('/orders')} disabled={saving}>Cancelar</button>
        {offerHash && (
          <button className="offer-print" onClick={() => window.print()} disabled={saving}>
            Imprimir o guardar en PDF
          </button>
        )}
        {offerHash && offer?.status === 'draft' && (
          <button className="offer-estado" onClick={() => cambiarEstado('sent')} disabled={saving}
                  title="Deja constancia de que ya se le ha mandado al cliente">
            Marcar como enviada
          </button>
        )}
        {offerHash && !offer?.order_hash && ['draft', 'sent'].includes(offer?.status) && (
          <button className="offer-rechazar" onClick={() => cambiarEstado('rejected')} disabled={saving}>
            El cliente la rechaza
          </button>
        )}
        {offerHash && offer?.status === 'rejected' && (
          <button className="offer-estado" onClick={() => cambiarEstado('draft')} disabled={saving}
                  title="Si fue un error, vuelve a borrador">
            Reabrir la oferta
          </button>
        )}
        {offerHash && !['rejected', 'expired'].includes(offer?.status) && (offer?.order_hash
          ? (
            <button className="offer-order-link" onClick={() => navigate(`/orders/${offer.order_hash}`)}>
              Ver el pedido
            </button>
          ) : (
            <button className="offer-convert" onClick={convertirEnPedido} disabled={saving || converting}>
              {converting ? 'Creando el pedido…' : 'El cliente la acepta: crear pedido'}
            </button>
          )
        )}
        <button className="offer-save" onClick={guardar} disabled={saving}>
          {saving ? 'Guardando…' : offerHash ? 'Guardar cambios' : 'Crear oferta'}
        </button>
      </div>
    </div>
  );
};

export default OfferPage;
